/*
 * Atomz search. Netflix-style overlay entry, Zappos-style results.
 *
 * SEARCH PROVIDER
 * ---------------
 * The overlay talks to one function: AtomzSearch.adapter.query(term).
 * Which backend it calls is set in the theme editor on the Atomz header
 * section (Search provider / Search endpoint), which writes:
 *
 *   window.ATOMZ_SEARCH_PROVIDER   'shopify' | 'atomz'
 *   window.ATOMZ_SEARCH_ENDPOINT   e.g. '/apps/atomz/autocomplete'
 *   window.ATOMZ_PRICE_UNIT        'cents' | 'major'
 *
 * With provider 'atomz' the adapter calls <endpoint>?q=<term>&limit=12 and
 * normalises the response. It accepts the common autocomplete shapes rather
 * than one fixed contract: products may arrive at the top level, under
 * results / items / hits / data.products, or in Shopify's
 * resources.results.products. Per product it reads title|name|product_title,
 * url or handle, vendor|brand, and image from any of the usual keys.
 *
 * If the Atomz response differs from all of those, replace normalise() below
 * or override AtomzSearch.adapter.query wholesale. Nothing else changes.
 */
(function () {
  'use strict';

  var config = {
    provider: window.ATOMZ_SEARCH_PROVIDER || 'shopify',
    endpoint: window.ATOMZ_SEARCH_ENDPOINT || '',
    priceUnit: window.ATOMZ_PRICE_UNIT || 'cents',
    shop: window.ATOMZ_SHOP || ''
  };

  /* Derive the currency prefix/suffix from a Liquid-rendered sample, e.g. "$0.00". */
  var moneyParts = function (sample) {
    var m = String(sample || '$0.00');
    var first = m.search(/\d/);
    var last = m.search(/\d(?![\s\S]*\d)/);
    return {
      pre: first > 0 ? m.slice(0, first) : first === 0 ? '' : '$',
      post: last >= 0 ? m.slice(last + 1).replace(/^[.,]\d+/, '') : ''
    };
  };

  var money = function (cents, parts) {
    if (cents === null || cents === undefined) return '';
    var n = (Number(cents) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var p = parts || { pre: '$', post: '' };
    return p.pre + n + p.post;
  };

  /*
   * Shopify's predictive endpoint reports price in cents ("12600"); most other
   * autocomplete APIs report major units (646 or "646.00"). Getting this wrong
   * is a silent 100x error, so the unit is a setting rather than a guess. A
   * value carrying a decimal point is always treated as major units.
   */
  var toCents = function (v) {
    if (v === null || v === undefined || v === '') return null;
    var s = String(v).replace(/[^0-9.\-]/g, '');
    if (!s) return null;
    if (s.indexOf('.') !== -1) return Math.round(parseFloat(s) * 100);
    var n = parseInt(s, 10);
    if (isNaN(n)) return null;
    return config.priceUnit === 'major' ? n * 100 : n;
  };

  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  /* ------------------------------------------------------------- adapter */

  var firstArray = function () {
    for (var i = 0; i < arguments.length; i++) {
      if (Array.isArray(arguments[i])) return arguments[i];
    }
    return [];
  };

  var pick = function (o) {
    for (var i = 1; i < arguments.length; i++) {
      var v = o[arguments[i]];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return '';
  };

  var productImage = function (p) {
    if (p.featured_image && p.featured_image.url) return p.featured_image.url;
    if (typeof p.featured_image === 'string') return p.featured_image;
    if (p.image && p.image.url) return p.image.url;
    if (typeof p.image === 'string') return p.image;
    if (Array.isArray(p.images) && p.images.length) {
      return typeof p.images[0] === 'string' ? p.images[0] : p.images[0].url || '';
    }
    /* Atomz returns every image the product has, newest first. */
    if (Array.isArray(p.featuredMedia) && p.featuredMedia.length) {
      return typeof p.featuredMedia[0] === 'string' ? p.featuredMedia[0] : p.featuredMedia[0].url || '';
    }
    return pick(p, 'image_url', 'thumbnail', 'imageUrl');
  };

  var productUrl = function (p) {
    var u = pick(p, 'url', 'link', 'product_url');
    if (u) return u;
    var h = pick(p, 'handle', 'slug');
    return h ? '/products/' + h : '#';
  };

  var normalise = function (data) {
    var d = data || {};
    var res = (d.resources && d.resources.results) || {};

    var raw = firstArray(
      res.products,
      d.products,
      d.results,
      d.items,
      d.hits,
      d.data && d.data.products,
      Array.isArray(d) ? d : null
    );

    var products = raw.map(function (p) {
      return {
        title: pick(p, 'titleForSuggester', 'title', 'name', 'product_title'),
        url: productUrl(p),
        vendor: pick(p, 'vendor', 'brand', 'manufacturer'),
        image: productImage(p),
        price: toCents(pick(p, 'price', 'min_price', 'price_min', 'lowest_price'))
      };
    });

    var rawQueries = firstArray(res.queries, d.queries, d.suggestions, d.completions);

    /*
     * Atomz hangs its query suggestions off each product as discoveryPrompts
     * -- "black block heel sandal", "adjustable ankle strap sandal" -- rather
     * than returning one list, so they are gathered here. Interleaved rather
     * than concatenated: the first product's five prompts are five ways of
     * describing one shoe, and taken in order they would fill the column with
     * that shoe. One from each product first spreads the list across the
     * result.
     */
    if (!rawQueries.length) {
      var rows = raw.map(function (p) {
        return Array.isArray(p.discoveryPrompts) ? p.discoveryPrompts : [];
      });
      var depth = Math.max.apply(null, [0].concat(rows.map(function (r) { return r.length; })));
      for (var col = 0; col < depth; col++) {
        for (var r = 0; r < rows.length; r++) {
          if (rows[r][col]) rawQueries.push(rows[r][col]);
        }
      }
    }

    var qSeen = {};
    var queries = rawQueries
      .map(function (q) {
        return typeof q === 'string' ? q : pick(q, 'text', 'title', 'query', 'value');
      })
      .filter(function (q) {
        if (!q) return false;
        var k = q.toLowerCase();
        if (qSeen[k]) return false;
        qSeen[k] = 1;
        return true;
      });

    var brands = [];
    var seen = {};
    var rawBrands = firstArray(d.brands, d.vendors);

    rawBrands.forEach(function (b) {
      var t = typeof b === 'string' ? b : pick(b, 'title', 'name', 'value');
      if (t && !seen[t]) {
        seen[t] = 1;
        brands.push({ title: t, url: '/search?q=' + encodeURIComponent(t) });
      }
    });

    if (!brands.length) {
      products.forEach(function (p) {
        if (p.vendor && !seen[p.vendor]) {
          seen[p.vendor] = 1;
          brands.push({ title: p.vendor, url: '/search?q=' + encodeURIComponent(p.vendor) });
        }
      });
    }

    return { products: products, brands: brands.slice(0, 8), queries: queries.slice(0, 6) };
  };

  /*
   * The Atomz autocomplete is a POST, not a query string.
   *
   * It was built here as GET <endpoint>?q=&limit=, which the service answers
   * with a 404 and an HTML error page -- so the overlay's live results were
   * never live, they were a promise that failed quietly every keystroke. The
   * shape below is the app's own, read out of its az-search-bar.js bundle and
   * checked against the service: POST {user_query, shop}, answered with a flat
   * array of products carrying titleForSuggester, handle, price in major
   * units, and featuredMedia.
   *
   * Shopify's own /search/suggest.json stays the other provider, unchanged.
   */
  var isAtomz = function () {
    return config.provider === 'atomz' && !!config.endpoint;
  };

  var adapter = {
    config: config,

    url: function (term) {
      if (isAtomz()) return config.endpoint;

      return (
        '/search/suggest.json?q=' + encodeURIComponent(term) +
        '&resources[type]=product,query' +
        '&resources[limit]=12' +
        '&resources[options][unavailable_products]=last'
      );
    },

    init: function (term) {
      if (!isAtomz()) {
        return { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
      }
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_query: term, shop: config.shop })
      };
    },

    query: function (term) {
      return fetch(this.url(term), this.init(term))
        .then(function (r) {
          if (!r.ok) throw new Error('search ' + r.status);
          return r.json();
        })
        .then(normalise);
    }
  };

  /* ------------------------------------------------------------- overlay */

  var Overlay = function (root) {
    this.root = root;
    this.input = root.querySelector('[data-az-input]');
    this.form = root.querySelector('[data-az-form]');
    this.live = root.querySelector('[data-az-live]');
    this.closeBtn = root.querySelector('[data-az-close]');
    this.money = moneyParts(root.getAttribute('data-az-money'));
    this.timer = null;
    this.rotator = null;
    this.pause = 0;
    this.lastTerm = '';

    try {
      this.prompts = JSON.parse(root.getAttribute('data-az-prompts') || '[]');
    } catch (e) {
      this.prompts = [];
    }

    this.bind();
  };

  Overlay.prototype.bind = function () {
    var self = this;

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function () {
        self.close();
      });
    }

    this.root.addEventListener('mousedown', function (e) {
      if (e.target === self.root) self.close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen()) self.close();
    });

    if (this.input) {
      this.input.addEventListener('input', function () {
        self.stopRotator();
        self.schedule(self.input.value.trim());
      });
    }

    if (this.form) {
      this.form.addEventListener('submit', function (e) {
        if (!self.input.value.trim()) {
          e.preventDefault();
          self.input.focus();
        }
      });
    }

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-az-suggest]'), function (btn) {
      btn.addEventListener('click', function () {
        self.stopRotator();
        self.input.value = btn.getAttribute('data-az-suggest');
        self.input.focus();
        self.schedule(self.input.value);
      });
    });

    /*
     * Both triggers read the seed. Focus used to hardcode '', which was
     * invisible while the results page had no overlay at all and destructive
     * the moment it got one: clicking the field on /search would have thrown
     * away the query the shopper came in with.
     *
     * A seeded open keeps the term, runs it through autocomplete and leaves
     * the suggestion chips in place above the results. An empty one runs the
     * typewriter instead.
     */
    Array.prototype.forEach.call(document.querySelectorAll('[data-az-open]'), function (el) {
      var seed = function () {
        return el.getAttribute('data-az-seed') || el.value || '';
      };
      el.addEventListener('click', function (e) {
        e.preventDefault();
        self.open(seed());
      });
      el.addEventListener('focus', function () {
        self.open(seed());
      });
    });
  };

  Overlay.prototype.isOpen = function () {
    return this.root.classList.contains('is-open');
  };

  Overlay.prototype.open = function (seed) {
    if (this.isOpen()) return;
    var self = this;
    this.root.hidden = false;
    requestAnimationFrame(function () {
      self.root.classList.add('is-open');
    });
    document.body.classList.add('az-locked');

    if (seed) {
      this.input.value = seed;
      this.schedule(seed);
    } else {
      this.startRotator();
    }

    setTimeout(function () {
      self.input.focus();
    }, 60);
  };

  Overlay.prototype.close = function () {
    var self = this;
    this.root.classList.remove('is-open');
    document.body.classList.remove('az-locked');
    this.stopRotator();
    setTimeout(function () {
      if (!self.isOpen()) self.root.hidden = true;
    }, 260);
  };

  /* Typewriter placeholder, the way the Netflix beta cycles its examples. */
  Overlay.prototype.startRotator = function () {
    if (!this.prompts.length || !this.input) return;

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.input.placeholder = this.prompts[0];
      return;
    }

    var self = this;
    var i = 0;
    var chars = 0;
    var erasing = false;

    this.stopRotator();
    this.pause = 0;

    this.rotator = setInterval(function () {
      if (self.input.value) {
        self.stopRotator();
        return;
      }

      var word = self.prompts[i];

      if (!erasing) {
        chars++;
        if (chars >= word.length) {
          chars = word.length;
          erasing = true;
          self.pause = 16;
        }
      } else if (self.pause > 0) {
        self.pause--;
      } else {
        chars -= 2;
        if (chars <= 0) {
          chars = 0;
          erasing = false;
          i = (i + 1) % self.prompts.length;
        }
      }

      self.input.placeholder = word.slice(0, chars);
    }, 60);
  };

  Overlay.prototype.stopRotator = function () {
    if (this.rotator) {
      clearInterval(this.rotator);
      this.rotator = null;
    }
  };

  Overlay.prototype.schedule = function (term) {
    var self = this;
    clearTimeout(this.timer);

    if (term.length < 2) {
      this.live.innerHTML = '';
      this.lastTerm = '';
      return;
    }

    if (term === this.lastTerm) return;

    this.live.innerHTML = '<div class="az-live__state"><div class="az-spin"></div></div>';
    this.timer = setTimeout(function () {
      self.run(term);
    }, 220);
  };

  Overlay.prototype.run = function (term) {
    var self = this;
    this.lastTerm = term;

    adapter
      .query(term)
      .then(function (data) {
        /* a slow response for an old keystroke must not overwrite a newer one */
        if (self.lastTerm !== term) return;
        self.render(term, data);
      })
      .catch(function () {
        if (self.lastTerm !== term) return;
        self.live.innerHTML =
          '<p class="az-live__state">Search is unavailable right now. ' +
          '<a class="az-live__more" href="/search?q=' + encodeURIComponent(term) + '">Open full results</a></p>';
      });
  };

  Overlay.prototype.render = function (term, data) {
    var self = this;
    var html = '';
    var products = data.products || [];
    var brands = data.brands || [];
    var queries = data.queries || [];

    if (!products.length && !brands.length && !queries.length) {
      this.live.innerHTML =
        '<p class="az-live__state">Nothing matched &ldquo;' + esc(term) +
        '&rdquo;. Try a brand, a model, or a size.</p>';
      return;
    }

    if (brands.length) {
      html += '<div class="az-live__group"><p class="az-live__heading">Brands</p><ul class="az-live__pills">';
      brands.forEach(function (b) {
        html += '<li><a class="az-live__pill" href="' + esc(b.url) + '">' + esc(b.title) + '</a></li>';
      });
      html += '</ul></div>';
    }

    if (queries.length) {
      html += '<div class="az-live__group"><p class="az-live__heading">Suggested searches</p><ul class="az-live__pills">';
      queries.forEach(function (t) {
        html += '<li><a class="az-live__pill" href="/search?q=' + encodeURIComponent(t) + '">' + esc(t) + '</a></li>';
      });
      html += '</ul></div>';
    }

    if (products.length) {
      html += '<div class="az-live__group"><p class="az-live__heading">Products</p><ul class="az-live__grid">';
      products.forEach(function (p) {
        html +=
          '<li><a class="az-live__card" href="' + esc(p.url) + '">' +
          '<div class="az-live__thumb">' +
          (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy" width="200" height="200">' : '') +
          '</div>' +
          (p.vendor ? '<p class="az-live__brand">' + esc(p.vendor) + '</p>' : '') +
          '<p class="az-live__name">' + esc(p.title) + '</p>' +
          '<p class="az-live__price">' + money(p.price, self.money) + '</p>' +
          '</a></li>';
      });
      html += '</ul></div>';
    }

    html +=
      '<p style="text-align:center;margin:0"><a class="az-live__more" href="/search?q=' +
      encodeURIComponent(term) + '">See all results for &ldquo;' + esc(term) + '&rdquo;</a></p>';

    this.live.innerHTML = html;
  };

  /* --------------------------------------------------------- wishlist ui */

  var Wishlist = {
    key: 'atomz:wishlist',

    read: function () {
      try {
        return JSON.parse(localStorage.getItem(this.key) || '[]');
      } catch (e) {
        return [];
      }
    },

    write: function (list) {
      try {
        localStorage.setItem(this.key, JSON.stringify(list));
      } catch (e) {
        /* private mode or storage blocked: the heart still toggles for the session */
      }
    },

    toggle: function (id) {
      var list = this.read();
      var i = list.indexOf(id);
      if (i === -1) list.push(id);
      else list.splice(i, 1);
      this.write(list);
      return i === -1;
    },

    init: function () {
      var self = this;
      var saved = this.read();

      Array.prototype.forEach.call(document.querySelectorAll('[data-az-heart]'), function (btn) {
        var id = btn.getAttribute('data-az-heart');
        if (saved.indexOf(id) !== -1) btn.setAttribute('aria-pressed', 'true');

        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var on = self.toggle(id);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          btn.setAttribute('aria-label', on ? 'Remove from favourites' : 'Add to favourites');
        });
      });
    }
  };

  /* ------------------------------------------------- mobile filter toggle */

  /*
   * Built at runtime so the section markup stays plain. Without JS the rail
   * simply renders open, which is the correct no-JS fallback.
   */
  var initFilters = function () {
    var rail = document.querySelector('.az-rail');
    if (!rail || rail.querySelector('.az-filter-toggle')) return;

    var body = document.createElement('div');
    body.className = 'az-rail__body';
    while (rail.firstChild) body.appendChild(rail.firstChild);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'az-filter-toggle';
    btn.setAttribute('aria-expanded', 'false');

    /*
     * The count comes off the rail, which Liquid stamps with the number of
     * active filter values. A shopper who has scrolled past a collapsed rail
     * otherwise has no way to tell the grid is already narrowed.
     */
    var n = parseInt(rail.getAttribute('data-az-filter-count'), 10) || 0;
    btn.textContent = n > 0 ? 'Filters (' + n + ')' : 'Filters';

    rail.appendChild(btn);
    rail.appendChild(body);

    btn.addEventListener('click', function () {
      var open = rail.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  };

  /* ------------------------------------------- type-to-narrow inside a facet */

  /*
   * A brand list a hundred rows long is a scrollbar, not a filter. Liquid only
   * prints the box on facets with a long tail, so short facets are untouched.
   * Matching is against a lowercased label Liquid stamped on each row, so the
   * filter never depends on how the label happens to be cased or marked up.
   */
  var initFacetFind = function () {
    var boxes = document.querySelectorAll('[data-az-facet-find]');
    Array.prototype.forEach.call(boxes, function (box) {
      var body = box.parentNode;
      if (!body) return;
      var rows = body.querySelectorAll('[data-az-facet-label]');

      box.addEventListener('input', function () {
        var q = box.value.trim().toLowerCase();
        var hits = 0;
        Array.prototype.forEach.call(rows, function (row) {
          var label = row.getAttribute('data-az-facet-label') || '';
          var show = q === '' || label.indexOf(q) !== -1;
          row.hidden = !show;
          if (show) hits++;
        });
        body.classList.toggle('az-facet__body--none', hits === 0);
      });
    });
  };

  /* ------------------------------------------------------------- sorting */

  var initSort = function () {
    var sel = document.querySelector('[data-az-sort]');
    if (!sel) return;
    sel.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sel.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  };

  /* ------------------------------------------------- placeholder rotator */

  /*
   * The same typewriter the overlay uses, on any input.
   *
   * The header field is the one a shopper actually looks at, and its
   * placeholder was a single static sentence. Cycling it turns the field into
   * an instruction: "Search for vanity", "Search for sink", one at a time.
   *
   * The prefix does not type. Only the term does, so the sentence reads as a
   * stable prompt with a changing object rather than a line of text being
   * spelled out from the first letter.
   *
   * Stops the moment the field has a value, so it never fights a shopper who
   * is typing, and it honours prefers-reduced-motion by showing the first
   * phrase and standing still.
   */
  var rotatePlaceholder = function (input, list, prefix) {
    if (!input || !list || !list.length) return null;

    /*
     * The space is added here, not asked for in the setting.
     *
     * The prefix shipped as "Search for " and the schema note said to keep the
     * trailing space. The theme editor trims trailing whitespace on save, so
     * the moment anyone opened the header settings the field started reading
     * "Search forboots I can walk in all day". A setting that only works while
     * nobody touches it is not a setting.
     */
    var pre = (prefix || '').replace(/\s+$/, '');
    if (pre) pre += ' ';

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      input.placeholder = pre + list[0];
      return null;
    }

    var i = 0, chars = 0, erasing = false, pause = 0;

    var timer = setInterval(function () {
      if (input.value) {
        input.placeholder = pre + list[i];
        return;
      }
      var word = list[i];
      if (!erasing) {
        chars++;
        if (chars >= word.length) { chars = word.length; erasing = true; pause = 16; }
      } else if (pause > 0) {
        pause--;
      } else {
        chars -= 2;
        if (chars <= 0) { chars = 0; erasing = false; i = (i + 1) % list.length; }
      }
      input.placeholder = pre + word.slice(0, chars);
    }, 60);

    return timer;
  };

  var initPlaceholders = function () {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-az-placeholders]'),
      function (input) {
        var list;
        try { list = JSON.parse(input.getAttribute('data-az-placeholders') || '[]'); }
        catch (e) { return; }
        rotatePlaceholder(input, list, input.getAttribute('data-az-placeholder-prefix') || '');
      }
    );
  };

  /* ---------------------------------------------------------------- boot */

  var boot = function () {
    if (window.AtomzSearch && window.AtomzSearch.overlay) return;
    var root = document.querySelector('[data-az-overlay]');
    window.AtomzSearch = { adapter: adapter, normalise: normalise, rotatePlaceholder: rotatePlaceholder, overlay: root ? new Overlay(root) : null };
    Wishlist.init();
    initFilters();
    initFacetFind();
    initSort();
    initPlaceholders();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
